/**
 * Comprehensive form validation utilities for database object creation/editing
 * Provides reusable validation functions and error handling
 */

import type { DatabaseConnection } from '@/stores/connectionStore'

// Common validation error types
export interface ValidationError {
  field: string
  message: string
  type: 'required' | 'format' | 'length' | 'unique' | 'compatibility' | 'sql' | 'custom'
}

export interface ValidationResult {
  isValid: boolean
  errors: Record<string, string>
  warnings: Record<string, string>
}

// Database-specific reserved words
const MYSQL_RESERVED_WORDS = [
  'index', 'primary', 'unique', 'key', 'table', 'column', 'select', 'insert', 
  'update', 'delete', 'from', 'where', 'order', 'group', 'by', 'database',
  'schema', 'view', 'trigger', 'procedure', 'function', 'user', 'grant',
  'revoke', 'create', 'drop', 'alter', 'add', 'modify', 'change'
]

const POSTGRESQL_RESERVED_WORDS = [
  'index', 'primary', 'unique', 'table', 'column', 'select', 'insert',
  'update', 'delete', 'from', 'where', 'order', 'group', 'by', 'create',
  'drop', 'alter', 'database', 'schema', 'view', 'trigger', 'procedure',
  'function', 'user', 'grant', 'revoke', 'sequence', 'extension'
]

const COMMON_SQL_RESERVED_WORDS = [
  'and', 'or', 'not', 'null', 'true', 'false', 'case', 'when', 'then',
  'else', 'end', 'union', 'join', 'inner', 'outer', 'left', 'right',
  'on', 'as', 'into', 'values', 'set', 'having', 'distinct', 'all',
  'exists', 'in', 'between', 'like', 'is', 'desc', 'asc', 'limit',
  'offset', 'count', 'sum', 'avg', 'min', 'max'
]

// Dangerous SQL patterns for injection prevention
const DANGEROUS_SQL_PATTERNS = [
  /DROP\s+/i,
  /DELETE\s+/i,
  /INSERT\s+/i,
  /UPDATE\s+/i,
  /CREATE\s+/i,
  /ALTER\s+/i,
  /EXEC\s+/i,
  /EXECUTE\s+/i,
  /--/,
  /\/\*/,
  /\*\//,
  /;.*$/,
  /union\s+select/i,
  /script\s*:/i,
  /javascript\s*:/i
]

/**
 * Base validation class providing common validation methods
 */
export class FormValidator {
  private connection: DatabaseConnection
  private existingNames: string[] = []

  constructor(connection: DatabaseConnection, existingNames: string[] = []) {
    this.connection = connection
    this.existingNames = existingNames.map(name => name.toLowerCase())
  }

  /**
   * Validate if a field is required and not empty
   */
  validateRequired(value: string, fieldName: string): string {
    if (!value || !value.trim()) {
      return `${fieldName} is required`
    }
    return ''
  }

  /**
   * Validate identifier format (database, table, column names, etc.)
   */
  validateIdentifier(value: string, fieldName: string, maxLength?: number): string {
    if (!value.trim()) {
      return `${fieldName} is required`
    }

    const trimmedValue = value.trim()

    // Check basic format
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedValue)) {
      return `${fieldName} must start with a letter or underscore and contain only letters, numbers, and underscores`
    }

    // Check length limits
    const dbMaxLength = this.getMaxIdentifierLength()
    const effectiveMaxLength = maxLength || dbMaxLength
    
    if (trimmedValue.length > effectiveMaxLength) {
      return `${fieldName} is too long (maximum ${effectiveMaxLength} characters for ${this.connection.type})`
    }

    // Check reserved words
    const reservedError = this.checkReservedWords(trimmedValue, fieldName)
    if (reservedError) return reservedError

    return ''
  }

  /**
   * Validate uniqueness against existing names
   */
  validateUniqueness(value: string, fieldName: string): string {
    if (!value.trim()) return ''
    
    const normalizedValue = value.trim().toLowerCase()
    if (this.existingNames.includes(normalizedValue)) {
      return `${fieldName} already exists`
    }
    return ''
  }

  /**
   * Validate SQL content (procedures, functions, triggers, etc.)
   */
  validateSQLContent(value: string, fieldName: string): string {
    if (!value.trim()) {
      return `${fieldName} is required`
    }

    // Check for dangerous patterns
    for (const pattern of DANGEROUS_SQL_PATTERNS) {
      if (pattern.test(value)) {
        return `${fieldName} contains potentially unsafe SQL`
      }
    }

    // Check for balanced parentheses
    const openParens = (value.match(/\(/g) || []).length
    const closeParens = (value.match(/\)/g) || []).length
    if (openParens !== closeParens) {
      return `Unbalanced parentheses in ${fieldName}`
    }

    // Check for balanced quotes
    const singleQuotes = (value.match(/'/g) || []).length
    const doubleQuotes = (value.match(/"/g) || []).length
    if (singleQuotes % 2 !== 0) {
      return `Unmatched single quotes in ${fieldName}`
    }
    if (doubleQuotes % 2 !== 0) {
      return `Unmatched double quotes in ${fieldName}`
    }

    // Check maximum length
    if (value.length > 10000) {
      return `${fieldName} is too long (maximum 10000 characters)`
    }

    return ''
  }

  /**
   * Validate email format
   */
  validateEmail(value: string, fieldName: string): string {
    if (!value.trim()) return ''
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value.trim())) {
      return `${fieldName} must be a valid email address`
    }
    return ''
  }

  /**
   * Validate password strength
   */
  validatePassword(value: string, fieldName: string): string {
    if (!value) {
      return `${fieldName} is required`
    }

    if (value.length < 8) {
      return `${fieldName} must be at least 8 characters long`
    }

    if (value.length > 128) {
      return `${fieldName} is too long (maximum 128 characters)`
    }

    // Check for basic complexity
    const hasLowerCase = /[a-z]/.test(value)
    const hasUpperCase = /[A-Z]/.test(value)
    const hasNumbers = /\d/.test(value)
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(value)

    const complexityCount = [hasLowerCase, hasUpperCase, hasNumbers, hasSpecialChars].filter(Boolean).length

    if (complexityCount < 3) {
      return `${fieldName} must contain at least 3 of: lowercase letters, uppercase letters, numbers, special characters`
    }

    return ''
  }

  /**
   * Validate numeric input with range
   */
  validateNumeric(value: string | number, fieldName: string, min?: number, max?: number): string {
    const numValue = typeof value === 'string' ? Number.parseFloat(value) : value
    
    if (Number.isNaN(numValue)) {
      return `${fieldName} must be a valid number`
    }

    if (min !== undefined && numValue < min) {
      return `${fieldName} must be at least ${min}`
    }

    if (max !== undefined && numValue > max) {
      return `${fieldName} must not exceed ${max}`
    }

    return ''
  }

  /**
   * Validate array/list fields
   */
  validateArray(value: unknown[], fieldName: string, minLength?: number, maxLength?: number): string {
    if (!Array.isArray(value)) {
      return `${fieldName} must be a valid list`
    }

    if (minLength !== undefined && value.length < minLength) {
      return `${fieldName} must have at least ${minLength} item${minLength !== 1 ? 's' : ''}`
    }

    if (maxLength !== undefined && value.length > maxLength) {
      return `${fieldName} cannot have more than ${maxLength} item${maxLength !== 1 ? 's' : ''}`
    }

    return ''
  }

  /**
   * Check for reserved words
   */
  private checkReservedWords(value: string, fieldName: string): string {
    const normalizedValue = value.toLowerCase()
    
    // Check common SQL reserved words
    if (COMMON_SQL_RESERVED_WORDS.includes(normalizedValue)) {
      return `"${value}" is a SQL reserved word and cannot be used as ${fieldName}`
    }

    // Check database-specific reserved words
    if (this.connection.type === 'mysql' && MYSQL_RESERVED_WORDS.includes(normalizedValue)) {
      return `"${value}" is a MySQL reserved word and cannot be used as ${fieldName}`
    } else if (this.connection.type === 'postgresql' && POSTGRESQL_RESERVED_WORDS.includes(normalizedValue)) {
      return `"${value}" is a PostgreSQL reserved word and cannot be used as ${fieldName}`
    }

    return ''
  }

  /**
   * Get maximum identifier length for the database type
   */
  private getMaxIdentifierLength(): number {
    switch (this.connection.type) {
      case 'mysql':
        return 64
      case 'postgresql':
        return 63
      default:
        return 63
    }
  }
}

/**
 * Creates a validation result aggregator for collecting validation errors and warnings
 */
export const createValidationAggregator = () => {
  const errors: Record<string, string> = {}
  const warnings: Record<string, string> = {}

  return {
    addError: (field: string, message: string): void => {
      if (message) {
        errors[field] = message
      }
    },

    addWarning: (field: string, message: string): void => {
      if (message) {
        warnings[field] = message
      }
    },

    getResult: (): ValidationResult => ({
      isValid: Object.keys(errors).length === 0,
      errors: { ...errors },
      warnings: { ...warnings }
    }),

    hasErrors: (): boolean => Object.keys(errors).length > 0,

    getFirstError: (): string | null => {
      const [firstErrorKey] = Object.keys(errors)
      return firstErrorKey ? errors[firstErrorKey] : null
    }
  }
}

/**
 * Validation hooks and utilities
 */
export const useFormValidation = (connection: DatabaseConnection, existingNames: string[] = []) => {
  const validator = new FormValidator(connection, existingNames)
  const aggregator = createValidationAggregator()

  return {
    validator,
    aggregator,
    validateField: (value: unknown, fieldName: string, validationType: string, options?: Record<string, unknown>) => {
      let error = ''
      
      switch (validationType) {
        case 'required': {
          const strValue = typeof value === 'string' ? value : JSON.stringify(value)
          error = validator.validateRequired(strValue, fieldName)
          break
        }
        case 'identifier': {
          const strValue = typeof value === 'string' ? value : JSON.stringify(value)
          const maxLength = typeof options?.maxLength === 'number' ? options.maxLength : undefined
          error = validator.validateIdentifier(strValue, fieldName, maxLength)
          break
        }
        case 'unique': {
          const strValue = typeof value === 'string' ? value : JSON.stringify(value)
          error = validator.validateUniqueness(strValue, fieldName)
          break
        }
        case 'sql': {
          const strValue = typeof value === 'string' ? value : JSON.stringify(value)
          error = validator.validateSQLContent(strValue, fieldName)
          break
        }
        case 'email': {
          const strValue = typeof value === 'string' ? value : JSON.stringify(value)
          error = validator.validateEmail(strValue, fieldName)
          break
        }
        case 'password': {
          const strValue = typeof value === 'string' ? value : JSON.stringify(value)
          error = validator.validatePassword(strValue, fieldName)
          break
        }
        case 'numeric': {
          const numOrString = (typeof value === 'number' || typeof value === 'string') ? value : Number(value)
          const min = typeof options?.min === 'number' ? options.min : undefined
          const max = typeof options?.max === 'number' ? options.max : undefined
          error = validator.validateNumeric(numOrString, fieldName, min, max)
          break
        }
        case 'array': {
          const arrValue = Array.isArray(value) ? value : []
          const minLength = typeof options?.minLength === 'number' ? options.minLength : undefined
          const maxLength = typeof options?.maxLength === 'number' ? options.maxLength : undefined
          error = validator.validateArray(arrValue as unknown[], fieldName, minLength, maxLength)
          break
        }
      }
      
      aggregator.addError(fieldName, error)
      return error
    }
  }
}

/**
 * Database-specific validation rules
 */
export const getDatabaseValidationRules = (connection: DatabaseConnection) => {
  const isMySQL = connection.type === 'mysql'
  const isPostgreSQL = connection.type === 'postgresql'
  
  return {
    // Maximum lengths
    maxIdentifierLength: isMySQL ? 64 : 63,
    maxTableNameLength: isMySQL ? 64 : 63,
    maxColumnNameLength: isMySQL ? 64 : 63,
    maxIndexNameLength: isMySQL ? 64 : 63,
    maxDatabaseNameLength: isMySQL ? 64 : 63,
    maxSchemaNameLength: isPostgreSQL ? 63 : 64,
    
    // Feature support
    supportsSchemas: isPostgreSQL,
    supportsPartialIndexes: isPostgreSQL,
    supportsGinIndexes: isPostgreSQL,
    supportsHashIndexes: isPostgreSQL,
    supportsExtensions: isPostgreSQL,
    supportsSequences: isPostgreSQL,
    supportsEvents: isMySQL,
    
    // Data type validation
    supportedColumnTypes: isMySQL ? 
      ['INT', 'BIGINT', 'VARCHAR', 'TEXT', 'DATETIME', 'TIMESTAMP', 'DECIMAL', 'FLOAT', 'BOOLEAN', 'JSON'] :
      ['INTEGER', 'BIGINT', 'VARCHAR', 'TEXT', 'TIMESTAMP', 'TIMESTAMPTZ', 'NUMERIC', 'REAL', 'BOOLEAN', 'JSONB', 'UUID'],
      
    // Constraint validation
    maxVarcharLength: isMySQL ? 65535 : 10485760,
    maxDecimalPrecision: isMySQL ? 65 : 1000,
    maxDecimalScale: isMySQL ? 30 : 1000
  }
}

/**
 * Common validation patterns for specific field types
 */
export const ValidationPatterns = {
  // Database object names
  IDENTIFIER: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
  
  // Network and connection
  HOST: /^[a-zA-Z0-9.-]+$/,
  PORT: /^[1-9]\d{0,4}$/,
  IP_ADDRESS: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
  
  // Data types
  DECIMAL: /^\d+(\.\d+)?$/,
  INTEGER: /^-?\d+$/,
  UUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  
  // Common formats
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  URL: /^https?:\/\/.+/,
  
  // File and path
  FILE_PATH: /^[^<>:"|?*]+$/,
  
  // Special characters for passwords
  SPECIAL_CHARS: /[!@#$%^&*(),.?":{}|<>]/
}
